package com.dotdrives.web.pages.donations

import com.dotdrives.controller.RecurringManagementController
import com.dotdrives.model.RecurringSchedule
import com.dotdrives.model.managementActions
import com.dotdrives.web.ui.useFlowState
import react.FC
import react.Props
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.style
import react.useState
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.text

external interface RecurringScheduleControlsProps : Props {
    var item: RecurringSchedule
    var controller: RecurringManagementController
}

val RecurringScheduleControls = FC<RecurringScheduleControlsProps> { props ->
    val item = props.item
    val actions = item.managementActions()
    val state = useFlowState(props.controller.state)
    val (open, setOpen) = useState(false)
    val (amount, setAmount) = useState(item.amount.toString())
    val blocked = state.busy || state.pendingOperation != null
    val confirmation = state.confirmation?.takeIf { it.schedule.id == item.id }
    if (actions.isNotEmpty()) {
        style { +recurringManagementStyles }
        div {
            className = ClassName("recurring-controls")
            button {
                type = ButtonType.buttonType
                className = ClassName("recurring-button")
                ariaExpanded = open
                onClick = { setOpen(!open); props.controller.dismiss() }
                +(if (open) "Close management" else "Manage recurring gift")
            }
            if (open) div {
                className = ClassName("recurring-editor")
                label {
                    +"Recurring gift amount (USD)"
                    input {
                        className = ClassName("recurring-search")
                        type = InputType.text
                        value = amount
                        disabled = blocked || confirmation != null
                        onChange = { setAmount(it.currentTarget.value) }
                    }
                }
                div {
                    className = ClassName("recurring-actions")
                    actions.forEach { action ->
                        button {
                            type = ButtonType.buttonType
                            className = ClassName("recurring-button" + if (action == "cancel") " recurring-danger" else "")
                            asDynamic()["data-action"] = action
                            disabled = blocked || confirmation != null
                            onClick = { props.controller.begin(item, action, if (action == "amount") amount else null) }
                            +when (action) {
                                "amount" -> "Change amount"
                                "pause" -> "Pause collection"
                                "resume" -> "Resume collection"
                                else -> "Cancel at period end"
                            }
                        }
                    }
                }
                confirmation?.let { change ->
                    div {
                        className = ClassName("recurring-confirm")
                        role = "region".unsafeCast<AriaRole>()
                        ariaLabel = "Confirm recurring gift change"
                        p { +"${change.schedule.giverName} · ${recurringCadence(change.schedule.interval)} gift" }
                        p {
                            +when (change.action) {
                                "amount" -> "Set the recurring charge to USD ${change.amount}. The billing cadence stays the same. No immediate proration."
                                "pause" -> "Pause collection until explicitly resumed. Invoices generated during the pause will be voided."
                                "resume" -> "Resume collection on the existing schedule."
                                else -> "Cancel this recurring gift at the end of the current billing period."
                            }
                        }
                        div {
                            className = ClassName("recurring-actions")
                            button {
                                type = ButtonType.buttonType
                                className = ClassName("recurring-button recurring-confirm-button")
                                asDynamic()["data-action"] = "confirm"
                                disabled = blocked
                                onClick = { props.controller.confirm() }
                                +"Confirm request"
                            }
                            button {
                                type = ButtonType.buttonType
                                className = ClassName("recurring-button")
                                disabled = state.busy
                                onClick = { props.controller.dismiss() }
                                +"Keep current schedule"
                            }
                        }
                    }
                }
            }
        }
    }
}

external interface RecurringManagementNoticeProps : Props { var controller: RecurringManagementController }

val RecurringManagementNotice = FC<RecurringManagementNoticeProps> { props ->
    val state = useFlowState(props.controller.state)
    if (state.error != null || state.message != null || state.pendingOperation != null) {
        style { +recurringManagementStyles }
        div {
            className = ClassName("recurring-management-notice")
            role = (if (state.error != null) "alert" else "status").unsafeCast<AriaRole>()
            state.error?.let { p { +it } }
            state.message?.let { p { +it } }
            if (state.pendingOperation != null) {
                p { +(if (state.busy) "Sending your recurring gift change…" else
                    "A recurring gift change needs a confirmed response. Retry the same request before making another change.") }
                button {
                    type = ButtonType.buttonType
                    className = ClassName("recurring-button")
                    disabled = state.busy
                    onClick = { props.controller.retry() }
                    +"Retry same request"
                }
            }
        }
    }
}

private val recurringManagementStyles = """
.recurring-controls {grid-column:1/-1;border-top:1px solid #E0E7EB;padding-top:12px}
.recurring-editor {margin-top:14px;display:flex;flex-direction:column;gap:12px}
.recurring-editor label {display:flex;flex-direction:column;align-items:flex-start;gap:7px;font-size:12px;font-weight:700;color:#33454F}
.recurring-editor input {width:180px;max-width:100%;box-sizing:border-box}
.recurring-actions {display:flex;gap:8px;flex-wrap:wrap}
.recurring-danger {color:#9A382F}
.recurring-confirm,.recurring-management-notice {padding:14px 16px;border:1px solid #D4DEE4;border-radius:10px;background:#F6F8F9;color:#33454F;font-size:13px;line-height:1.6}
.recurring-confirm p,.recurring-management-notice p {margin:0 0 10px}
.recurring-confirm-button {background:#B84818;color:white;border-color:#B84818}
""".trimIndent()
